/**
 * Money and time, formatted the way this audience reads them.
 *
 * Turkish locale, but the CURRENCY comes from the race — a race run on a site
 * that pays in dollars says dollars, whoever is looking at it. Guessing the
 * currency from the viewer's locale would quietly rewrite the prize.
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      // Whole numbers for the big figures, which is how a prize pool is
      // advertised: "$5.000", never "$5.000,00".
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    // An unknown currency code must not take the page down with it.
    return `${new Intl.NumberFormat('tr-TR').format(amount)} ${currency}`;
  }
}

export function formatWager(amount: number, currency: string): string {
  return formatMoney(Math.round(amount), currency);
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True once the target has passed. */
  isOver: boolean;
}

export function countdownTo(targetIso: string, now: number): Countdown {
  const remaining = Date.parse(targetIso) - now;

  if (!Number.isFinite(remaining) || remaining <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isOver: true };
  }

  const seconds = Math.floor(remaining / 1000);

  return {
    days: Math.floor(seconds / 86_400),
    hours: Math.floor((seconds % 86_400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    isOver: false,
  };
}

export function formatDateRange(startIso: string, endIso: string): string {
  const formatter = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long' });

  return `${formatter.format(new Date(startIso))} – ${formatter.format(new Date(endIso))}`;
}
